import { InformationCircleIcon } from '@heroicons/react/outline';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WhatsappWabaAccount } from '../../hooks/useWhatsappTemplates';
import { WhatsappTemplate, WhatsappTemplateStatus } from '../../types/whatsappTemplates';
import { WhatsappAccountSelector } from './WhatsappAccountSelector';
import { WhatsappTemplateListStats } from './WhatsappTemplateListStats';

const DISPLAYED_STATUSES: WhatsappTemplateStatus[] = ['approved', 'inReview', 'rejected', 'draft'];

const buildTemplateStats = (templates: WhatsappTemplate[]) => {
  const totalByStatus = templates.reduce<Record<WhatsappTemplateStatus, number>>(
    (acc, item) => {
      acc[item.status] += 1;
      return acc;
    },
    { approved: 0, inReview: 0, rejected: 0, draft: 0, pending: 0 }
  );

  return DISPLAYED_STATUSES.map((key) => ({
    key,
    value: totalByStatus[key],
  }));
};

type WhatsappTemplateWabaSectionProps = {
  accounts: WhatsappWabaAccount[];
  selectedWabaId: string | null;
  onWabaChange: (wabaId: string) => void;
  templates: WhatsappTemplate[];
  isLoading?: boolean;
};

export const WhatsappTemplateWabaSection: FC<WhatsappTemplateWabaSectionProps> = ({
  accounts,
  selectedWabaId,
  onWabaChange,
  templates,
  isLoading,
}) => {
  const { t } = useTranslation('campaign');

  const templateStats = useMemo(() => buildTemplateStats(templates), [templates]);

  return (
    <div className='space-y-3'>
      <WhatsappAccountSelector
        accounts={accounts}
        selectedWabaId={selectedWabaId}
        onChange={onWabaChange}
        isLoading={isLoading}
      />

      <p className='flex items-center gap-1.5 text-sm text-secondary-600'>
        <InformationCircleIcon className='size-4 shrink-0 text-quaternary-400' aria-hidden='true' />
        {t('whatsappTemplates.wabaScopedHint')}
      </p>

      <WhatsappTemplateListStats stats={templateStats} />
    </div>
  );
};
